import {
  COMMA_DECIMAL_SEPARATOR,
  DEFAULT_FRACTION_DIGITS,
  ENGLISH_LOCALE_CODE,
  FALLBACK_DECIMAL_SEPARATOR,
  FALLBACK_THOUSAND_SEPARATOR,
  POINT_DECIMAL_SEPARATOR,
  WEIGHT_DECIMAL_DIGITS,
} from "~/utils/constants";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";

export class NumberFormatter extends Intl.NumberFormat {
  constructor(locales?: string | string[], options?: Intl.NumberFormatOptions) {
    super(locales, options);
  }

  static getDecimalSeparator(): string {
    const { decimalSeparator } = getRuntimeConfig();
    if (decimalSeparator) {
      return decimalSeparator;
    }
    return FALLBACK_DECIMAL_SEPARATOR;
  }

  static getThousandSeparator(): string {
    const { thousandSeparator } = getRuntimeConfig();
    if (thousandSeparator) {
      return thousandSeparator;
    }
    return FALLBACK_THOUSAND_SEPARATOR;
  }

  static getDecimalSeparatorName(): "point" | "comma" {
    if (NumberFormatter.getDecimalSeparator() == FALLBACK_DECIMAL_SEPARATOR) {
      return COMMA_DECIMAL_SEPARATOR;
    }
    return POINT_DECIMAL_SEPARATOR;
  }

  static getFractionDigits = (currencyCode: string | null) => {
    const { currencyIso, currencyDecimalDigits } = getRuntimeConfig();
    if (currencyCode === currencyIso) {
      return currencyDecimalDigits;
    }
    return DEFAULT_FRACTION_DIGITS;
  };

  static getFormatter(config?: Intl.NumberFormatOptions) {
    return new NumberFormatter(ENGLISH_LOCALE_CODE, config) as NumberFormatter;
  }

  static getDefaultFormatter(): NumberFormatter {
    return new NumberFormatter(ENGLISH_LOCALE_CODE, {
      maximumFractionDigits: DEFAULT_FRACTION_DIGITS,
    }) as NumberFormatter;
  }

  static getPriceFormatter(): NumberFormatter {
    const { currencyIso } = getRuntimeConfig();
    const fractionDigits = NumberFormatter.getFractionDigits(currencyIso);
    return new NumberFormatter(ENGLISH_LOCALE_CODE, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) as NumberFormatter;
  }

  static getWeightFormatterKg() {
    return new NumberFormatter(ENGLISH_LOCALE_CODE, {
      minimumFractionDigits: WEIGHT_DECIMAL_DIGITS,
      maximumFractionDigits: WEIGHT_DECIMAL_DIGITS,
    }) as NumberFormatter;
  }

  static getWeightFormatterG() {
    // SCHAEFF-15861 - Grams should be shown without decimals
    return new NumberFormatter(ENGLISH_LOCALE_CODE, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) as NumberFormatter;
  }

  public format(value: number): string {
    return super
      .format(value)
      .replaceAll(",", "#")
      .replaceAll(".", "?")
      .replaceAll("#", NumberFormatter.getThousandSeparator())
      .replaceAll("?", NumberFormatter.getDecimalSeparator());
  }
}

export function formatPercent(value: number | undefined, missingValueSymbol: string = "-") {
  if (value === undefined) {
    return missingValueSymbol;
  }
  return NumberFormatter.getDefaultFormatter().format(value * 100) + "%";
}
